import { useEffect, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { TotpQrCode } from "@charisol-spark/client-core";
import { authApi, getErrorMessage } from "@/lib/api";
import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";
import { Label } from "@/components/ui/Label";
import { Alert } from "@/components/ui/Alert";

/**
 * The enrol → scan/enter → confirm sequence for authenticator-app (TOTP)
 * MFA, shared by the voluntary path (Settings → Security's
 * AuthenticatorAppSection) and the forced first-access path (AdminMfaGate).
 * Both call the same two endpoints (`POST /auth/mfa/enrol`, `POST
 * /auth/mfa/verify`) and show the same QR + setup key + 6-digit form — only
 * the chrome around this (an inline card vs. a full-screen step) differs,
 * so that's the only thing left to each caller.
 *
 * QR + setup key: the `otpauth://` URI from `/auth/mfa/enrol` is rendered as
 * a scannable QR code (via `TotpQrCode`, @charisol-spark/client-core —
 * generated entirely client-side, the secret never leaves the browser) with
 * the raw setup key kept visible and copyable alongside it, for
 * authenticator apps/devices where scanning isn't practical.
 */
export function AuthenticatorEnrollmentFlow({
  onEnrolled,
  autoStart = false,
}: {
  /** Called once `/auth/mfa/verify` succeeds. */
  onEnrolled?: () => void;
  /**
   * Start enrolment immediately on mount instead of waiting for a "Set up
   * authenticator app" click. Used by the forced first-access gate, which
   * has already decided this step is unavoidable — an extra button press
   * to begin something already on the only screen the admin can see adds
   * nothing.
   */
  autoStart?: boolean;
}) {
  const queryClient = useQueryClient();
  const [started, setStarted] = useState(autoStart);
  const [code, setCode] = useState("");
  const [copied, setCopied] = useState(false);

  const enrolMutation = useMutation({
    mutationFn: () => authApi.enrolMfa(),
  });

  const verifyMutation = useMutation({
    mutationFn: (c: string) => authApi.verifyMfa(c),
    onSuccess: () => {
      setCode("");
      queryClient.invalidateQueries({ queryKey: ["auth", "mfa", "status"] });
      onEnrolled?.();
    },
  });

  useEffect(() => {
    if (started && !enrolMutation.data && !enrolMutation.isPending) {
      enrolMutation.mutate();
    }
    // enrolMutation is a stable useMutation return value whose identity
    // changes every render; depending on it would re-fire this on every
    // render instead of once per `started` transition.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [started]);

  const handleCopySecret = async () => {
    if (!enrolMutation.data?.secretCode) return;
    try {
      await navigator.clipboard.writeText(enrolMutation.data.secretCode);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (_err) {
      // Clipboard access can be denied (permissions, non-secure context);
      // the secret is still visible as selectable text, so this is a
      // convenience failure, not a blocking one.
    }
  };

  const handleVerify = (e: React.FormEvent) => {
    e.preventDefault();
    if (code.length !== 6) return;
    verifyMutation.mutate(code);
  };

  if (!started) {
    return (
      <Button onClick={() => setStarted(true)} loading={enrolMutation.isPending}>
        Set up authenticator app
      </Button>
    );
  }

  if (enrolMutation.isPending || (!enrolMutation.data && !enrolMutation.isError)) {
    return <p className="text-sm text-muted-foreground">Starting enrolment…</p>;
  }

  if (enrolMutation.isError) {
    return (
      <div className="space-y-3">
        <Alert variant="error">
          {getErrorMessage(
            enrolMutation.error,
            "Could not start enrolment. Please try again."
          )}
        </Alert>
        <Button variant="outline" onClick={() => enrolMutation.mutate()}>
          Try again
        </Button>
      </div>
    );
  }

  const data = enrolMutation.data!;

  return (
    <div className="space-y-4">
      <p className="text-sm text-muted-foreground">
        Scan this with your authenticator app (Google Authenticator,
        1Password, Authy, or similar), or enter the setup key manually, then
        confirm with the 6-digit code it generates.
      </p>

      <TotpQrCode otpauthUrl={data.otpauthUrl} />

      <div className="space-y-2">
        <Label htmlFor="mfaSecret">Setup key</Label>
        <div className="flex items-center gap-2">
          <Input
            id="mfaSecret"
            readOnly
            value={data.secretCode}
            onFocus={(e) => e.currentTarget.select()}
            className="font-mono"
          />
          <Button type="button" variant="outline" onClick={handleCopySecret}>
            {copied ? "Copied" : "Copy"}
          </Button>
        </div>
      </div>

      <form onSubmit={handleVerify} className="space-y-3">
        <div className="space-y-2">
          <Label htmlFor="mfaVerifyCode">6-digit code</Label>
          <Input
            id="mfaVerifyCode"
            type="text"
            inputMode="numeric"
            autoComplete="one-time-code"
            placeholder="123456"
            maxLength={6}
            value={code}
            onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
          />
        </div>

        {verifyMutation.isError && (
          <Alert variant="error">
            {getErrorMessage(
              verifyMutation.error,
              "That code didn't match. Please try again."
            )}
          </Alert>
        )}

        <Button
          type="submit"
          disabled={code.length !== 6}
          loading={verifyMutation.isPending}
        >
          Confirm
        </Button>
      </form>
    </div>
  );
}
